/**
 * AssignedTasksPanel — Todo list "Compiti Assegnati" tra admin ARCHI-HUB.
 *
 * Un admin (creator) crea un impegno per un altro admin (assignee).
 * Regole:
 *  - Il compito è visibile SOLO a creator e assignee.
 *  - L'assignee marca "Fatto" (status=done).
 *  - Il creator elimina (o riapre) il compito quando vuole.
 *
 * Endpoint backend: /admin/assigned-tasks (GET/POST/PUT complete-reopen/DELETE).
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ClipboardList, Plus, Check, RotateCcw, Trash2, X,
  UserCheck, User, Calendar as CalIcon, AlertCircle, Loader2,
  MessageSquare, Send, ChevronDown, ChevronUp, Eye,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAdminNotificationEvents, useAdminNotificationsCtx } from "../context/AdminNotificationsContext";

const tsOf = (s) => { const n = Date.parse(s || ""); return Number.isNaN(n) ? 0 : n; };

// Il task ha eventi in entrata (nuovo compito / risposta / completamento di
// altri) più recenti dell'ultimo "Visto" dell'utente corrente?
function taskUnseenForMe(task, myId) {
  if (!myId) return false;
  const seen = tsOf((task.viewed_by || {})[myId]);
  if (task.assignee_id === myId && task.creator_id !== myId && tsOf(task.created_at) > seen) return true;
  if (task.creator_id === myId && task.assignee_id !== myId && task.status === "done" && tsOf(task.completed_at) > seen) return true;
  for (const c of task.comments || []) {
    if (c.author_id !== myId && tsOf(c.created_at) > seen) return true;
  }
  return false;
}

const CATS_STYLE = {
  pending: {
    badge: "bg-amber-100 text-amber-800 border-amber-300",
    dot: "bg-amber-500",
    label: "Da fare",
  },
  done: {
    badge: "bg-emerald-100 text-emerald-800 border-emerald-300",
    dot: "bg-emerald-500",
    label: "Fatto",
  },
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    // input format YYYY-MM-DD
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !d) return iso;
    return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
  } catch {
    return iso;
  }
}

function todayIsoDate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatCommentTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const diffMin = Math.round((now - d) / 60000);
    if (diffMin < 1) return "adesso";
    if (diffMin < 60) return `${diffMin} min fa`;
    const sameDay = d.toDateString() === now.toDateString();
    const hh = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    if (sameDay) return `oggi ${hh}`;
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return `ieri ${hh}`;
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} ${hh}`;
  } catch {
    return "";
  }
}

export default function AssignedTasksPanel({ currentUser }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("assigned_to_me"); // default: mostra subito i compiti PER ME
  const { sseOpen } = useAdminNotificationsCtx();

  const myId = currentUser?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/assigned-tasks");
      setTasks(r.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Impossibile caricare i compiti");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const r = await api.get("/admin/assigned-tasks/admins");
      setAdmins(r.data || []);
    } catch {
      /* silente */
    }
  }, []);

  useEffect(() => {
    load();
    loadAdmins();
    // Polling di fallback: attivo solo se SSE è down (evita traffico doppio)
    if (sseOpen) return undefined;
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load, loadAdmins, sseOpen]);

  // ---- Notifiche real-time via SSE ---------------------------------
  const showBrowserNotification = useCallback((title, body) => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    try {
      const n = new Notification(title, {
        body: (body || "").slice(0, 160),
        tag: "archihub-assigned-task",
        renotify: true,
      });
      n.onclick = () => {
        try { window.focus(); } catch { /* ignore */ }
        n.close();
      };
    } catch { /* ignore */ }
  }, []);

  const isVisibleRef = useRef(true);
  useEffect(() => {
    const onVis = () => { isVisibleRef.current = !document.hidden; };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useAdminNotificationEvents("assigned_task.", (event) => {
      const type = event?.type;
      if (!type || !type.startsWith("assigned_task.")) return;

      if (type === "assigned_task.deleted") {
        setTasks((prev) => prev.filter((t) => t.id !== event.task_id));
        return;
      }

      // ---- Commenti (add/delete) — patch inline sul task locale ----
      if (type === "assigned_task.comment_added" && event.comment) {
        let taskInvolvesMe = false;
        setTasks((prev) => prev.map((t) => {
          if (t.id !== event.task_id) return t;
          taskInvolvesMe = t.creator_id === myId || t.assignee_id === myId;
          const already = (t.comments || []).some((c) => c.id === event.comment.id);
          if (already) return t;
          return { ...t, comments: [...(t.comments || []), event.comment] };
        }));
        // Notifica se la risposta è di un ALTRO
        if (event.comment.author_id !== myId && taskInvolvesMe) {
          showBrowserNotification(
            `${event.comment.author_name} ha risposto`,
            event.comment.text,
          );
          if (isVisibleRef.current) toast(`${event.comment.author_name}: ${event.comment.text.slice(0, 80)}`);
        }
        return;
      }
      if (type === "assigned_task.comment_deleted") {
        setTasks((prev) => prev.map((t) => {
          if (t.id !== event.task_id) return t;
          return { ...t, comments: (t.comments || []).filter((c) => c.id !== event.comment_id) };
        }));
        return;
      }

      const t = event.task;
      if (!t) return;

      // Escludi eventi non pertinenti a questo admin (sicurezza extra: il
      // backend già filtra, ma facciamo anche noi doppia verifica)
      if (t.creator_id !== myId && t.assignee_id !== myId) return;

      // Merge/replace nel state
      setTasks((prev) => {
        const withRole = {
          ...t,
          viewer_role:
            t.creator_id === myId && t.assignee_id === myId
              ? "both"
              : t.creator_id === myId
              ? "creator"
              : "assignee",
        };
        const idx = prev.findIndex((x) => x.id === t.id);
        if (idx === -1) return [withRole, ...prev];
        const copy = prev.slice();
        copy[idx] = withRole;
        return copy;
      });

      // Feedback UI/browser solo se l'evento è FATTO DA UN ALTRO admin
      const madeByMe = event.task?.completed_by_id === myId
        || (type === "assigned_task.created" && t.creator_id === myId);
      if (madeByMe) return;

      if (type === "assigned_task.created" && t.assignee_id === myId) {
        showBrowserNotification(
          `Nuovo compito da ${t.creator_name}`,
          t.description,
        );
        if (isVisibleRef.current) toast.success(`Nuovo compito da ${t.creator_name}`);
      } else if (type === "assigned_task.completed" && t.creator_id === myId) {
        showBrowserNotification(
          `${t.assignee_name} ha completato un compito`,
          t.description,
        );
        if (isVisibleRef.current) toast.success(`${t.assignee_name} ha completato: ${t.description.slice(0, 60)}`);
      } else if (type === "assigned_task.reopened" && t.assignee_id === myId) {
        if (isVisibleRef.current) toast(`Compito riaperto da ${t.creator_name}`);
      }
    });

  const filtered = tasks.filter((t) => {
    if (filter === "assigned_to_me") return t.assignee_id === myId;
    if (filter === "created_by_me") return t.creator_id === myId;
    return true;
  });

  // Ordina: pending prima (per due_date crescente), poi done (recenti prima)
  const sorted = [...filtered].sort((a, b) => {
    if (a.status !== b.status) return a.status === "pending" ? -1 : 1;
    if (a.status === "pending") {
      const aDate = a.due_date || "9999-12-31";
      const bDate = b.due_date || "9999-12-31";
      return aDate.localeCompare(bDate);
    }
    return (b.completed_at || "").localeCompare(a.completed_at || "");
  });

  const pendingCount = tasks.filter((t) => t.status === "pending").length;
  const forMeCount = tasks.filter((t) => t.status === "pending" && t.assignee_id === myId).length;

  const handleMarkViewed = async (tid) => {
    try {
      const { data: updated } = await api.put(`/admin/assigned-tasks/${tid}/view`);
      if (updated) setTasks((prev) => prev.map((t) => (t.id === tid ? updated : t)));
    } catch {
      toast.error("Errore nel segnare come visto");
    }
  };

  const handleComplete = async (tid) => {
    setBusyId(tid);
    try {
      await api.put(`/admin/assigned-tasks/${tid}/complete`);
      await load();
      toast.success("Compito segnato come fatto");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore");
    } finally {
      setBusyId(null);
    }
  };

  const handleReopen = async (tid) => {
    setBusyId(tid);
    try {
      await api.put(`/admin/assigned-tasks/${tid}/reopen`);
      await load();
      toast.success("Compito riaperto");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (tid) => {
    if (!window.confirm("Eliminare questo compito?")) return;
    setBusyId(tid);
    try {
      await api.delete(`/admin/assigned-tasks/${tid}`);
      await load();
      toast.success("Compito eliminato");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore");
    } finally {
      setBusyId(null);
    }
  };

  const handleAddComment = async (tid, text) => {
    try {
      const r = await api.post(`/admin/assigned-tasks/${tid}/comments`, { text });
      // Aggiorna in locale con il task ritornato (include il commento)
      const updated = r.data?.task;
      if (updated) {
        setTasks((prev) => prev.map((t) => (t.id === tid ? { ...t, comments: updated.comments || [] } : t)));
      }
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore invio risposta");
      return false;
    }
  };

  const handleDeleteComment = async (tid, cid) => {
    try {
      await api.delete(`/admin/assigned-tasks/${tid}/comments/${cid}`);
      setTasks((prev) => prev.map((t) => (
        t.id !== tid ? t : { ...t, comments: (t.comments || []).filter((c) => c.id !== cid) }
      )));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore");
    }
  };

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 shadow-sm"
      data-testid="assigned-tasks-panel"
    >
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-gold/15 p-1.5 rounded-md">
            <ClipboardList size={16} className="text-amber-800" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold flex items-center gap-1.5">
              Todo Team
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${sseOpen ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`}
                title={sseOpen ? "Notifiche real-time attive" : "Connessione in attesa..."}
                data-testid="sse-status-dot"
              />
            </div>
            <h2 className="font-display text-base text-ink leading-tight">Compiti assegnati</h2>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowNewModal(true)}
          className="bg-ink hover:bg-slate-800 text-white text-[10px] tracking-wider uppercase font-semibold px-2.5 py-1.5 rounded flex items-center gap-1 transition"
          data-testid="assigned-tasks-new"
        >
          <Plus size={12} /> Nuovo
        </button>
      </div>

      {/* Filtri */}
      <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap gap-1">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} count={pendingCount} testid="filter-all">
          Tutti
        </FilterPill>
        <FilterPill active={filter === "assigned_to_me"} onClick={() => setFilter("assigned_to_me")} count={forMeCount} testid="filter-mine">
          Per me
        </FilterPill>
        <FilterPill active={filter === "created_by_me"} onClick={() => setFilter("created_by_me")} testid="filter-created">
          Creati
        </FilterPill>
      </div>

      <div className="max-h-[calc(100vh-16rem)] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-center text-slate1 text-sm">
            <Loader2 size={16} className="inline-block animate-spin mr-2" /> Caricamento...
          </div>
        ) : sorted.length === 0 ? (
          <div className="p-6 text-center">
            <ClipboardList size={28} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
            <p className="text-sm text-slate1">
              {filter === "all" ? "Nessun compito attivo" : filter === "assigned_to_me" ? "Nessun compito assegnato a te" : "Non hai creato compiti"}
            </p>
            <button
              type="button"
              onClick={() => setShowNewModal(true)}
              className="mt-3 text-xs text-gold hover:text-amber-700 font-semibold uppercase tracking-wider"
              data-testid="assigned-tasks-empty-new"
            >
              + Crea il primo
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sorted.map((t) => (
              <TaskItem
                key={t.id}
                task={t}
                myId={myId}
                busy={busyId === t.id}
                onComplete={() => handleComplete(t.id)}
                onMarkViewed={() => handleMarkViewed(t.id)}
                onReopen={() => handleReopen(t.id)}
                onDelete={() => handleDelete(t.id)}
                onAddComment={(text) => handleAddComment(t.id, text)}
                onDeleteComment={(cid) => handleDeleteComment(t.id, cid)}
              />
            ))}
          </ul>
        )}
      </div>

      {showNewModal && (
        <NewTaskModal
          admins={admins}
          currentUser={currentUser}
          onClose={() => setShowNewModal(false)}
          onCreated={async () => {
            setShowNewModal(false);
            await load();
            toast.success("Compito creato");
          }}
        />
      )}
    </div>
  );
}

function FilterPill({ active, onClick, count, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[10px] tracking-wider uppercase font-semibold px-2 py-1 rounded transition ${
        active ? "bg-ink text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      }`}
      data-testid={testid}
    >
      {children}
      {count > 0 && (
        <span className={`ml-1.5 px-1 rounded ${active ? "bg-white/25" : "bg-white text-ink"}`}>{count}</span>
      )}
    </button>
  );
}

function TaskItem({ task, myId, busy, onComplete, onMarkViewed, onReopen, onDelete, onAddComment, onDeleteComment }) {
  const style = CATS_STYLE[task.status] || CATS_STYLE.pending;
  const iAmAssignee = task.assignee_id === myId;
  const iAmCreator = task.creator_id === myId;
  const iAmBoth = iAmAssignee && iAmCreator;

  const isOverdue = task.status === "pending" && task.due_date && task.due_date < todayIsoDate();
  const comments = task.comments || [];
  const commentsCount = comments.length;

  // Espandi automaticamente se ci sono già risposte, altrimenti chiuso
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const submitReply = async (e) => {
    e?.preventDefault();
    const text = replyText.trim();
    if (!text || sending) return;
    setSending(true);
    const ok = await onAddComment(text);
    setSending(false);
    if (ok) setReplyText("");
  };

  return (
    <li
      className={`p-3 hover:bg-cream/30 transition ${task.status === "done" ? "opacity-70" : ""}`}
      data-testid={`assigned-task-${task.id}`}
    >
      <div className="flex items-start gap-2">
        <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${style.dot}`} />
        <div className="flex-1 min-w-0">
          <div
            className={`text-sm text-ink font-medium leading-snug ${task.status === "done" ? "line-through text-slate-500" : ""}`}
            data-testid={`task-description-${task.id}`}
          >
            {task.description}
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px]">
            {/* Chi → Chi */}
            {iAmBoth ? (
              <span className="inline-flex items-center gap-1 text-slate-600">
                <User size={10} /> Per te (auto)
              </span>
            ) : iAmAssignee ? (
              <span className="inline-flex items-center gap-1 text-slate-700">
                <User size={10} /> Da <strong className="font-semibold">{task.creator_name}</strong>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-slate-700">
                <UserCheck size={10} /> A <strong className="font-semibold">{task.assignee_name}</strong>
              </span>
            )}

            {task.due_date && (
              <span
                className={`inline-flex items-center gap-1 ${
                  isOverdue ? "text-rose-700 font-semibold" : "text-slate-500"
                }`}
              >
                <CalIcon size={10} />
                {formatDate(task.due_date)}
                {isOverdue && <AlertCircle size={10} className="inline" />}
              </span>
            )}

            {task.priority === "high" && task.status === "pending" && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 font-semibold">
                URGENTE
              </span>
            )}

            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${style.badge}`}>
              {style.label}
            </span>

            {/* DA VEDERE → VISTO: marca gli aggiornamenti come visti; quando
                tutto è visto la mascotte AI torna normale */}
            {taskUnseenForMe(task, myId) ? (
              <button
                type="button"
                onClick={onMarkViewed}
                disabled={busy}
                title="Clicca per segnare come visto"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 font-semibold hover:bg-amber-200 transition disabled:opacity-40"
                data-testid={`task-toview-${task.id}`}
              >
                <Eye size={10} /> DA VEDERE
              </button>
            ) : (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200"
                data-testid={`task-viewed-${task.id}`}
              >
                <Eye size={10} /> Visto
              </span>
            )}
          </div>
        </div>

        {/* Azioni */}
        <div className="flex flex-col gap-1 items-end">
          {task.status === "pending" && (iAmAssignee || iAmCreator) && (
            <button
              type="button"
              onClick={onComplete}
              disabled={busy}
              title="Segna come fatto"
              className="p-1.5 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 transition disabled:opacity-40"
              data-testid={`task-complete-${task.id}`}
            >
              <Check size={12} />
            </button>
          )}
          {task.status === "done" && iAmCreator && (
            <button
              type="button"
              onClick={onReopen}
              disabled={busy}
              title="Riapri compito"
              className="p-1.5 rounded bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition disabled:opacity-40"
              data-testid={`task-reopen-${task.id}`}
            >
              <RotateCcw size={12} />
            </button>
          )}
          {iAmCreator && (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              title="Elimina"
              className="p-1.5 rounded bg-slate-50 hover:bg-rose-50 text-slate-500 hover:text-rose-700 border border-slate-200 hover:border-rose-200 transition disabled:opacity-40"
              data-testid={`task-delete-${task.id}`}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>

      {/* --- Risposte / Commenti ------------------------------------- */}
      <div className="mt-2 ml-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-[10px] tracking-wider uppercase font-semibold text-slate-500 hover:text-ink flex items-center gap-1 transition"
          data-testid={`task-toggle-replies-${task.id}`}
        >
          <MessageSquare size={11} />
          {commentsCount > 0 ? `${commentsCount} ${commentsCount === 1 ? "risposta" : "risposte"}` : "Rispondi"}
          {expanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>

        {expanded && (
          <div className="mt-2 space-y-2" data-testid={`task-replies-${task.id}`}>
            {commentsCount > 0 && (
              <ul className="space-y-1.5">
                {comments.map((c) => {
                  const mine = c.author_id === myId;
                  const canDelete = mine || iAmCreator;
                  return (
                    <li
                      key={c.id}
                      className={`flex gap-2 items-start rounded-md p-2 text-xs ${mine ? "bg-amber-50 border border-amber-100" : "bg-slate-50 border border-slate-100"}`}
                      data-testid={`task-reply-${c.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-slate-600 font-semibold flex items-center gap-1.5">
                          <User size={9} />
                          {c.author_name}
                          <span className="text-slate-400 font-normal">·</span>
                          <span className="text-slate-400 font-normal">{formatCommentTime(c.created_at)}</span>
                        </div>
                        <div className="text-ink whitespace-pre-wrap break-words leading-snug mt-0.5">
                          {c.text}
                        </div>
                      </div>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => onDeleteComment(c.id)}
                          className="p-1 rounded text-slate-400 hover:text-rose-600 hover:bg-white transition flex-shrink-0"
                          title="Elimina risposta"
                          data-testid={`task-reply-delete-${c.id}`}
                        >
                          <Trash2 size={10} />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            <form onSubmit={submitReply} className="flex gap-1.5 items-start">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    submitReply(e);
                  }
                }}
                rows={2}
                maxLength={2000}
                placeholder="Scrivi una risposta… (Ctrl+Invio per inviare)"
                className="flex-1 px-2 py-1.5 border border-slate-200 rounded text-xs focus:ring-1 focus:ring-gold focus:border-gold outline-none resize-none"
                data-testid={`task-reply-input-${task.id}`}
              />
              <button
                type="submit"
                disabled={sending || !replyText.trim()}
                className="p-2 rounded bg-ink hover:bg-slate-800 text-white transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                title="Invia risposta"
                data-testid={`task-reply-submit-${task.id}`}
              >
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              </button>
            </form>
          </div>
        )}
      </div>
    </li>
  );
}

function NewTaskModal({ admins, currentUser, onClose, onCreated }) {
  const [assigneeId, setAssigneeId] = useState(admins?.[0]?.id || "");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("normal");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Default assignee: preferisci un altro admin (non me stesso), altrimenti me
    if (!admins || admins.length === 0) return;
    const other = admins.find((a) => a.id !== currentUser?.id);
    setAssigneeId(other ? other.id : admins[0].id);
  }, [admins, currentUser?.id]);

  // Blocca scroll body mentre la modal è aperta
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const desc = description.trim();
    if (!desc) {
      toast.error("Scrivi la descrizione del compito");
      return;
    }
    if (!assigneeId) {
      toast.error("Seleziona a chi assegnare il compito");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/assigned-tasks", {
        assignee_id: assigneeId,
        description: desc,
        due_date: dueDate || null,
        priority,
      });
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Errore creazione");
    } finally {
      setSaving(false);
    }
  };

  // Portal su document.body per uscire dallo stacking-context del pannello sticky
  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="assigned-tasks-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5 animate-in slide-in-from-bottom-4"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg text-ink">Nuovo compito</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded"
            aria-label="Chiudi"
            data-testid="modal-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label htmlFor="task-assignee" className="block text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold mb-1">
              Assegna a
            </label>
            <select
              id="task-assignee"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none bg-white"
              data-testid="new-task-assignee"
              required
            >
              {admins.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} {a.id === currentUser?.id ? "(io)" : ""} · {a.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="task-desc" className="block text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold mb-1">
              Descrizione
            </label>
            <textarea
              id="task-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Es: Fare preventivo per cliente Rossi, cucina Boffi con isola centrale…"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none resize-none"
              data-testid="new-task-description"
              required
            />
            <div className="text-[10px] text-slate-400 text-right mt-0.5">{description.length}/1000</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="task-due" className="block text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold mb-1">
                Scadenza (opzionale)
              </label>
              <input
                id="task-due"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none"
                data-testid="new-task-duedate"
              />
            </div>
            <div>
              <label htmlFor="task-prio" className="block text-[10px] tracking-[0.18em] uppercase text-slate1 font-semibold mb-1">
                Priorità
              </label>
              <select
                id="task-prio"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none bg-white"
                data-testid="new-task-priority"
              >
                <option value="normal">Normale</option>
                <option value="high">Urgente</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-5">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-ink hover:bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider py-2.5 rounded flex items-center justify-center gap-1.5 transition disabled:opacity-50"
            data-testid="new-task-submit"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Crea compito
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-5 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-xs font-semibold uppercase tracking-wider rounded transition disabled:opacity-50"
            data-testid="new-task-cancel"
          >
            Annulla
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
